import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { CallPathV1 } from '@frontend/api-call-path';
import { CallPathMapping } from '@frontend/call-path-mapping';
import { getDisplayName } from '@frontend/get-display-name';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { PhonesIllustration, PhonesTranscriptionNotAvailable } from '@frontend/phones#svg';
import { theme } from '@frontend/theme';
import { ContentLoader, Text } from '@frontend/design-system';
import { useCallInsightController } from '../../hooks/use-call-insight-controller';
import { useCallInsightPanelShallowStore } from '../../hooks/use-call-insight-panel-store';
import { ScreenBaseProps, ScreenType } from '../../types';
import { NavigationButtons } from '../navigation-buttons';
import { ScreenLayout } from '../screen-layout';

type CallPathMappingScreenProps = ScreenBaseProps<ScreenType.CALL_PATH_MAPPING>;
const IMAGE_SIZE = 300;
const SOURCE = 'callHistory';

export const CallPathMappingScreen = ({ data, panelStyles, onClose, setScreen }: CallPathMappingScreenProps) => {
  const { closeContext, contexts } = useCallInsightPanelShallowStore('closeContext', 'contexts');
  const { t } = useTranslation('calls');

  const dataToUse = data?.call ?? contexts[SOURCE].selectedCall;
  const callPathQuery = {
    callStartTime: dataToUse?.startedAt ?? '',
    channelId: dataToUse?.channelId ?? '',
  };

  const callStartTimeFormatted = dayjs(dataToUse?.startedAt).format('MMM DD YYYY, hh:mm A');

  const callFrom = useMemo(() => {
    const { firstName, lastName, preferredName } = dataToUse?.person ?? {};
    const { fullName } = getDisplayName({
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      preferredName,
    });

    let displayName;
    if (!!fullName) {
      displayName = fullName;
    } else if (dataToUse?.callerName && isNaN(+dataToUse.callerName)) {
      displayName = dataToUse.callerName;
    } else {
      displayName = formatPhoneNumber(dataToUse?.callerNumber);
    }
    return displayName.trim();
  }, [dataToUse?.person, dataToUse?.callerName, dataToUse?.callerNumber]);

  const handleClose = useCallback(() => {
    closeContext();
    onClose?.();
  }, [closeContext, onClose]);

  const {
    data: callPathDetails,
    status,
    refetch,
    isFetchedAfterMount,
    isRefetching,
  } = CallPathV1.CallPathQueries.useGetCallPathQuery(callPathQuery);

  const callStatus = callPathDetails?.callStatus ?? dataToUse?.status ?? '';
  const hasAllocatedTimePassed = checkForAfterTimeLimit(dataToUse?.startedAt);
  const hasCallPathEvents = (callPathDetails?.callEvents?.length ?? 0) > 0;

  useEffect(() => {
    if (status !== 'success' || hasCallPathEvents || isFetchedAfterMount || isRefetching) {
      return;
    }

    refetch();
  }, [hasCallPathEvents, isFetchedAfterMount, isRefetching, refetch, status]);

  const statusToComponentMap: Record<typeof status, JSX.Element | null> = {
    loading: <ContentLoader show />,
    error: (
      <StateDisplay
        image={<PhonesTranscriptionNotAvailable height={IMAGE_SIZE} customFallback={<></>} />}
        text={t('Call Path is unavailable due to system error.')}
      />
    ),
    success: hasAllocatedTimePassed ? (
      <StateDisplay
        image={<PhonesIllustration height={IMAGE_SIZE} customFallback={<></>} />}
        text={t("It doesn't look like there's any data to show here")}
      />
    ) : (
      <StateDisplay
        image={<PhonesIllustration height={IMAGE_SIZE} customFallback={<></>} />}
        text={t("Hold tight, we're working on getting this data")}
      />
    ),
    idle: null,
  };

  const renderContent = () =>
    hasCallPathEvents ? <CallPathMapping callPathDetails={callPathDetails} /> : statusToComponentMap[status] ?? null;

  const { goToNextCall, goToPreviousCall } = useCallInsightController({
    source: SOURCE,
    view: ScreenType.CALL_PATH_MAPPING,
  });

  const panelContext = contexts[SOURCE];
  const hasNextCall = !!panelContext?.nextCallItem;
  const hasPreviousCall = !!panelContext?.previousCallItem;

  return (
    <ScreenLayout
      panelStyles={[panelStyles, containerStyles]}
      header={CallPathMapping.Header({
        onBack: () => setScreen({ screenType: ScreenType.CALL_INSIGHTS }),
        onClose: handleClose,
        callFrom,
        callStartTime: callStartTimeFormatted ?? callPathDetails?.startedAt,
        callStatus,
        aiCallMetadata: dataToUse?.aiMetadata,
      })}
      content={renderContent()}
      footer={
        <NavigationButtons
          disableNext={!hasNextCall}
          disablePrevious={!hasPreviousCall}
          onNextClick={goToNextCall}
          onPreviousClick={goToPreviousCall}
          trackingIdBase='call-insight-panel'
        />
      }
      footerStyles={footerStyles}
      contentStyles={contentStyles}
      trackingId='CallPathOverviewPanel'
    />
  );
};

const StateDisplay = ({ image, text }: { image: React.ReactNode; text: string }) => (
  <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), alignItems: 'center' }}>
    {image}
    <Text textAlign='center' color='light' weight='bold'>
      {text}
    </Text>
  </div>
);

/* Backend suggest adding a 5 min time gap to wait for the processes to finish if no data is available due to current limitations */
function checkForAfterTimeLimit(timestamp: string | undefined) {
  if (!timestamp) return;
  const now = dayjs();
  const givenTime = dayjs(timestamp);
  const fiveMinutesAfter = givenTime.add(5, 'minute');
  return now.isAfter(fiveMinutesAfter);
}

const footerStyles = css`
  padding: ${theme.spacing(2)};
`;

const containerStyles = css`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const contentStyles = css`
  padding: ${theme.spacing(2)};
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: ${theme.spacing(2)};
  background-color: ${theme.colors.neutral5};

  & > .event-box-item::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 16px;
    background-color: ${theme.colors.neutral70};
  }

  & > .event-box-item.analytics-info-icon::after {
    top: 20px;
  }

  & > .event-box-item:last-of-type::after {
    display: none;
  }
`;
